import { formatDuration } from "@bandwith/time";
import { Button } from "@bandwith/ui";
import { Clock, MessageCircle } from "lucide-react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTimestampReference } from "../hooks/useTimestampReference";
import type { FeedbackItem } from "../lib/api";
import { api } from "../lib/api";
import { useSession } from "../lib/auth";
import { useSignInRedirect } from "../lib/useSignInRedirect";
import type { FeedbackCodeMirrorRef } from "./FeedbackCodeMirror";

// Lazy-load CodeMirror editor to reduce initial bundle (~30KB)
const FeedbackCodeMirror = lazy(() =>
  import("./FeedbackCodeMirror").then((m) => ({
    default: m.FeedbackCodeMirror,
  })),
);

/**
 * What the feedback attaches to: a song, or a setlist. A setlist target also
 * names the recordings a timestamp reference may point at (its songs' takes),
 * since "currently playing" can't be tied to a single song there.
 */
export type FeedbackTarget =
  | { songId: string }
  | { setlistId: string; recordingIds: string[] };

type Props = {
  target: FeedbackTarget;
  onFeedbackCreated?: (feedback: FeedbackItem) => void;
  /** Pre-filled anchor markup from text selection in ChordProRenderer */
  pendingAnchor?: string | null;
  /** Called after the anchor has been consumed/rendered */
  onAnchorConsumed?: () => void;
};

export function FeedbackForm({
  target,
  onFeedbackCreated,
  pendingAnchor,
  onAnchorConsumed,
}: Props) {
  const { session } = useSession();
  const signInRedirect = useSignInRedirect();
  const currentUserId = session?.user?.id ?? null;
  const currentUserName = session?.user?.name ?? null;
  const currentUserImage = session?.user?.image ?? null;
  const isSignedIn = !!session?.user;
  const editorRef = useRef<FeedbackCodeMirrorRef>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [body, setBody] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Timestamp reference for linking to current playback position
  const { currentTime, isPlayingTarget, getTimestampReference } =
    useTimestampReference(
      "songId" in target
        ? { songId: target.songId }
        : { recordingIds: target.recordingIds },
    );

  const insertTimestampReference = useCallback(() => {
    const reference = getTimestampReference();
    if (reference) {
      editorRef.current?.insertText(reference);
    }
  }, [getTimestampReference]);

  // Handle pending anchor from "Add Feedback at Selection"
  useEffect(() => {
    if (pendingAnchor) {
      setBody(pendingAnchor);
      setIsExpanded(true);
      onAnchorConsumed?.();
      // Focus the editor after it expands
      setTimeout(() => {
        editorRef.current?.focus();
      }, 0);
    }
  }, [pendingAnchor, onAnchorConsumed]);

  const handleSubmit = async () => {
    if (!body.trim()) {
      setError("Feedback cannot be empty");
      return;
    }

    if (!currentUserId) {
      setError("You must be signed in to leave feedback");
      return;
    }

    const trimmedBody = body.trim();
    const userId = currentUserId;

    setIsPending(true);
    try {
      const response = await api.feedback.$post({
        json:
          "songId" in target
            ? { songId: target.songId, body: trimmedBody }
            : { setlistId: target.setlistId, body: trimmedBody },
      });
      if (!response.ok) {
        setError("Failed to create feedback");
        return;
      }
      const created = await response.json();
      // Build the full feedback object for local state
      const now = new Date().toISOString();
      const newFeedback: FeedbackItem = {
        id: created.feedbackId,
        songId: "songId" in target ? target.songId : null,
        setlistId: "setlistId" in target ? target.setlistId : null,
        authorId: userId,
        body: trimmedBody,
        createdAt: now,
        updatedAt: now,
        author: {
          id: userId,
          name: currentUserName ?? "Unknown",
          email: null,
          emailVerified: null,
          image: currentUserImage,
          role: "user",
          createdAt: now,
          updatedAt: now,
        },
      };
      onFeedbackCreated?.(newFeedback);
      setBody("");
      setError(null);
      setIsExpanded(false);
    } catch {
      setError("Failed to create feedback");
    } finally {
      setIsPending(false);
    }
  };

  const handleCollapse = () => {
    setBody("");
    setError(null);
    setIsExpanded(false);
  };

  // Handle expanding the form with auto-insert of timestamp when playing
  const handleExpand = useCallback(() => {
    // Auto-insert timestamp if playing a recording feedback can point at
    if (isPlayingTarget) {
      const reference = getTimestampReference();
      if (reference) {
        setBody(reference);
      }
    }
    setIsExpanded(true);
    // Focus the editor after it expands
    setTimeout(() => {
      editorRef.current?.focus();
    }, 0);
  }, [isPlayingTarget, getTimestampReference]);

  if (!isSignedIn) {
    return (
      <div className="rounded-lg border border-dashed border-border-subtle bg-bg-secondary/50 p-4 text-center">
        <p className="text-sm text-text-muted">
          <button
            type="button"
            onClick={() => signInRedirect(window.location.pathname)}
            className="text-amber hover:underline"
          >
            Sign in
          </button>{" "}
          to leave feedback
        </p>
      </div>
    );
  }

  if (!isExpanded) {
    return (
      <Button variant="secondary" onClick={handleExpand}>
        <MessageCircle className="h-4 w-4" aria-hidden="true" />
        {isPlayingTarget
          ? `Add Feedback @ ${formatDuration(currentTime)}`
          : "Add Feedback"}
      </Button>
    );
  }

  return (
    <div className="rounded-lg border border-border-subtle bg-bg-secondary p-4">
      <Suspense
        fallback={
          <div className="min-h-[80px] animate-pulse rounded-lg bg-bg-tertiary" />
        }
      >
        <FeedbackCodeMirror
          ref={editorRef}
          value={body}
          onChange={(val) => {
            setBody(val);
            if (error) setError(null);
          }}
          onSubmit={handleSubmit}
          placeholder="Share your feedback..."
          disabled={isPending}
          hasError={!!error}
          autoFocus={isExpanded}
        />
      </Suspense>
      {error && (
        <p className="mt-2 text-sm text-red-muted" role="alert">
          {error}
        </p>
      )}
      <div className="mt-3 flex items-center justify-between">
        <div>
          <Button
            variant="secondary"
            onClick={insertTimestampReference}
            disabled={isPending || !isPlayingTarget}
            title={
              isPlayingTarget
                ? undefined
                : "Play a recording to add a timestamp"
            }
            data-testid="timestamp-button"
          >
            <Clock className="h-4 w-4" aria-hidden="true" />
            {isPlayingTarget
              ? `Link @ ${formatDuration(currentTime)}`
              : "Link @ Timestamp"}
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleCollapse}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? "Posting..." : "Post"}
          </Button>
        </div>
      </div>
    </div>
  );
}
